import { store } from '../util/store';

/**
 * Service to control toast notification suppression based on page context.
 *
 * Suppression Rules:
 * 1. Error toasts are NEVER suppressed (always shown)
 * 2. Info/success toasts are suppressed on RCC page during data loading
 * 3. All toasts are shown on Dashboard and other pages
 */
class ToastSuppressionService {
  // Pages where info/success toasts should be suppressed
  suppressedPages = ['Revenue Command Center'];

  /**
   * Get the current active page context from Redux state.
   * Checks multiple page indicators to determine the active view.
   * RCC can be accessed from multiple parent pages, each with their own sub-page state.
   */
  getActivePageContext() {
    const state = store.getState();
    const {
      currentPage,
      otherInsightsPage,
      growthModelingPage,
      enhancedInsightsPage,
      pipelineBuildandSuficencyPage,
      pipelineVelocityandConversionPage,
      marketIntelandGrowthLeversPage,
      salesProductivityPage,
      growthScienceInsightsPage,
      pipelineProgressionPage,
      user,
    } = state.app;

    // RCC can also be rendered as the "Home" page depending on the user's first nav section.
    // In that case, treat the active context as RCC so suppression rules still apply.
    const isRccHome =
      currentPage === 'Home' &&
      Array.isArray(user?.elements?.[0]?.charts) &&
      user.elements[0].charts.some((c) => c?.Chart_Name === 'Revenue Command Center');
    if (isRccHome) {
      return 'Revenue Command Center';
    }

    // Check all sub-page states for RCC
    const subPageStates = [
      otherInsightsPage,
      growthModelingPage,
      enhancedInsightsPage,
      pipelineBuildandSuficencyPage,
      pipelineVelocityandConversionPage,
      marketIntelandGrowthLeversPage,
      salesProductivityPage,
      growthScienceInsightsPage,
      pipelineProgressionPage,
    ];

    // If any sub-page is set to a suppressed page, return that
    for (const subPage of subPageStates) {
      if (subPage && this.suppressedPages.includes(subPage)) {
        return subPage;
      }
    }

    return currentPage;
  }

  /**
   * Determine if a toast should be shown based on type and page context.
   * @param {Object} alert - The alert object { type, message }
   * @returns {boolean} - true if toast should be shown, false to suppress
   */
  shouldShowToast(alert) {
    // Error toasts are ALWAYS shown
    if (alert.type === 'error') {
      return true;
    }

    // Suppress info/success toasts on pages in the suppressed list
    const activeContext = this.getActivePageContext();
    const shouldSuppress = this.suppressedPages.includes(activeContext);
    const shouldShow = !shouldSuppress;

    // Debug logging
    const state = store.getState();
    console.log('[ToastSuppression] Decision:', {
      alertType: alert.type,
      alertMessage: alert.message,
      activeContext,
      shouldSuppress,
      shouldShow,
      suppressedPages: this.suppressedPages,
      currentPage: state.app.currentPage,
      growthModelingPage: state.app.growthModelingPage,
    });

    return shouldShow;
  }

  /**
   * Add a page to the suppression list.
   * @param {string} pageName - Page name to add
   */
  addSuppressedPage(pageName) {
    if (!this.suppressedPages.includes(pageName)) {
      this.suppressedPages.push(pageName);
    }
  }

  /**
   * Remove a page from the suppression list.
   * @param {string} pageName - Page name to remove
   */
  removeSuppressedPage(pageName) {
    this.suppressedPages = this.suppressedPages.filter(p => p !== pageName);
  }
}

export const toastSuppressionService = new ToastSuppressionService();
